import { Activity, CheckCircle, Clock, DollarSign, XCircle } from "lucide-react";
import { useCallback, useState } from "react";
import { StatCard } from "@/components/shared/StatCard";
import { RankedList } from "@/components/monitoring/RankedList";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { usePolling } from "@/hooks/usePolling";
import { formatCurrency, formatDuration } from "@/lib/chart-helpers";

type AgentEvent = {
  ts: number;
  runId: string;
  sessionKey: string;
  agent: string;
  model: string;
  provider: string;
  durationMs: number;
  tokens: { input: number; output: number; cacheRead: number; cacheWrite: number };
  cost: number;
  toolCalls: number;
  success: boolean;
  error?: string;
  isSubagent: boolean;
  isXorgSession: boolean;
  handlerOrgId?: string;
};

type AgentSummary = {
  totalRuns: number;
  successCount: number;
  errorCount: number;
  successRate: number;
  avgDurationMs: number;
  totalCost: number;
  totalTokens: number;
  byModel: Record<string, { runs: number; cost: number; tokens: number }>;
  byAgent: Record<string, { runs: number; cost: number }>;
};

type AgentData = {
  events: AgentEvent[];
  summary: AgentSummary;
};

function formatTokens(n: number): string {
  if (n >= 1_000_000) {return `${(n / 1_000_000).toFixed(1)}M`;}
  if (n >= 1_000) {return `${(n / 1_000).toFixed(1)}K`;}
  return String(n);
}

export function AgentActivity() {
  const { rpc, state } = useGatewayRpc();
  const [timeframe, setTimeframe] = useState(24);
  const [modelFilter, setModelFilter] = useState("");
  const [sessionFilter, setSessionFilter] = useState("");

  const fetcher = useCallback(async (): Promise<AgentData> => {
    const since = Date.now() - timeframe * 3_600_000;
    const res = await rpc<{ events: AgentEvent[]; summary: AgentSummary }>("metrics.agents", {
      since,
      model: modelFilter || undefined,
      sessionKey: sessionFilter || undefined,
    });
    return res;
  }, [rpc, timeframe, modelFilter, sessionFilter]);

  const { data, loading } = usePolling(fetcher, 10_000, { enabled: state === "connected" });

  const events = data?.events ?? [];
  const summary = data?.summary;

  // Unique models for filter dropdown
  const models = [...new Set(events.map((e) => e.model))].sort();

  // Tool call frequency
  const toolCounts = new Map<string, number>();
  for (const e of events) {
    if (e.toolCalls > 0) {
      const key = e.agent || "default";
      toolCounts.set(key, (toolCounts.get(key) ?? 0) + e.toolCalls);
    }
  }
  const toolRanking = [...toolCounts.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  if (state !== "connected" && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold">Agent Activity</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {state === "connecting" ? "Connecting to gateway..." : "Gateway disconnected"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Agent Activity</h1>
        <select
          value={timeframe}
          onChange={(e) => setTimeframe(Number(e.target.value))}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
        >
          <option value={1}>Last 1 hour</option>
          <option value={6}>Last 6 hours</option>
          <option value={24}>Last 24 hours</option>
          <option value={72}>Last 3 days</option>
          <option value={168}>Last 7 days</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard title="Total Runs" value={String(summary?.totalRuns ?? 0)} />
        <StatCard
          title="Success Rate"
          value={`${((summary?.successRate ?? 0) * 100).toFixed(0)}%`}
          sparkColor={
            (summary?.successRate ?? 0) >= 0.9
              ? "var(--color-healthy)"
              : (summary?.successRate ?? 0) >= 0.5
                ? "var(--color-warning)"
                : "var(--color-critical)"
          }
        />
        <StatCard title="Avg Duration" value={formatDuration(summary?.avgDurationMs ?? 0)} />
        <StatCard title="Total Cost" value={formatCurrency(Math.round((summary?.totalCost ?? 0) * 100))} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={modelFilter}
          onChange={(e) => setModelFilter(e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
        >
          <option value="">All models</option>
          {models.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Filter by session key..."
          value={sessionFilter}
          onChange={(e) => setSessionFilter(e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-indigo-400 dark:border-slate-700 dark:bg-slate-900"
        />
      </div>

      {/* Event Feed + Tool Ranking */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Event Feed */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Activity className="h-4 w-4" /> Recent Runs ({events.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">No agent runs in this timeframe</p>
            ) : (
              <div className="max-h-[400px] space-y-2 overflow-y-auto">
                {events
                  .slice()
                  .sort((a, b) => b.ts - a.ts)
                  .slice(0, 50)
                  .map((evt) => (
                    <div
                      key={`${evt.runId}-${evt.ts}`}
                      className="flex items-start gap-3 rounded-md border p-2.5 text-sm dark:border-slate-800"
                    >
                      {evt.success ? (
                        <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                      ) : (
                        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{evt.model.split("-").slice(0, 3).join("-")}</span>
                          <Badge variant="secondary" className="text-[10px]">{evt.provider}</Badge>
                          {evt.isXorgSession && (
                            <Badge variant="outline" className="border-amber-500 text-[10px] text-amber-600 dark:text-amber-400">
                              xorg{evt.handlerOrgId ? `: ${evt.handlerOrgId}` : ""}
                            </Badge>
                          )}
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDuration(evt.durationMs)}
                          </span>
                          <span>{formatTokens(evt.tokens.input + evt.tokens.output)} tokens</span>
                          {evt.cost > 0 && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="h-3 w-3" />
                              {formatCurrency(Math.round(evt.cost * 100))}
                            </span>
                          )}
                          {evt.toolCalls > 0 && <span>{evt.toolCalls} tools</span>}
                          <span className="text-slate-400">{new Date(evt.ts).toLocaleTimeString()}</span>
                        </div>
                        {evt.error && (
                          <p className="mt-1 text-xs text-red-500 dark:text-red-400">{evt.error}</p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tool Call Frequency */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Tool Calls by Agent</CardTitle>
          </CardHeader>
          <CardContent>
            {toolRanking.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">No tool calls</p>
            ) : (
              <RankedList items={toolRanking} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* By-Model Breakdown Table */}
      {summary && Object.keys(summary.byModel).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">By Model</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 dark:border-slate-700">
                  <th className="pb-2 pr-4">Model</th>
                  <th className="pb-2 pr-4 text-right">Runs</th>
                  <th className="pb-2 pr-4 text-right">Tokens</th>
                  <th className="pb-2 text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(summary.byModel)
                  .sort(([, a], [, b]) => b.runs - a.runs)
                  .map(([model, v]) => (
                    <tr key={model} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="py-2 pr-4 font-mono text-xs">{model}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{v.runs}</td>
                      <td className="py-2 pr-4 text-right tabular-nums text-slate-500">{formatTokens(v.tokens)}</td>
                      <td className="py-2 text-right tabular-nums">{formatCurrency(Math.round(v.cost * 100))}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {loading && !data && <p className="text-center text-sm text-slate-400">Loading agent data...</p>}
    </div>
  );
}
